import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FlaskConical, Play, RefreshCw, Server, ChevronRight, Sparkles,
  CheckCircle2, XCircle, ClipboardCopy, Clock, History, Info, FileText,
} from "lucide-react";

/**
 * POS Playground — pick a vendor + operation, edit the request, send it to
 * either the vendor's sandbox or the built-in Mock POS, then inspect the
 * raw response + our-side field-mapping diff.
 */
export default function POSPlaygroundPage() {
  const [params, setParams] = useSearchParams();
  const initialVendor = params.get("vendor");

  const [vendors, setVendors] = useState([]);
  const [ops, setOps] = useState([]);
  const [vendorId, setVendorId] = useState(initialVendor || "");
  const [op, setOp] = useState("member_lookup");
  const [payloadText, setPayloadText] = useState("{}");
  const [endpointOverride, setEndpointOverride] = useState("");
  const [mock, setMock] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const selectedVendor = useMemo(() => vendors.find((v) => v.id === vendorId), [vendors, vendorId]);
  const selectedOp = useMemo(() => ops.find((o) => o.key === op), [ops, op]);

  const reload = async () => {
    try {
      const [v, o] = await Promise.all([
        api.get("/pos-vendors"),
        api.get("/pos-vendors/operations"),
      ]);
      setVendors(v.data.vendors || []);
      setOps(o.data.operations || []);
      if (!initialVendor && v.data.vendors?.length && !vendorId) {
        setVendorId(v.data.vendors[0].id);
      }
    } catch (e) {
      toast.error("Failed to load: " + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  // Load a sample request whenever vendor/op changes
  useEffect(() => {
    if (!vendorId || !op) return;
    (async () => {
      try {
        const r = await api.get(`/pos-vendors/${vendorId}/sample-request`, { params: { operation: op } });
        setPayloadText(JSON.stringify(r.data.request_payload || {}, null, 2));
        setEndpointOverride(r.data.endpoint || "");
        setResult(null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Sample fetch failed");
      }
    })();
  }, [vendorId, op]);

  // Persist deep-linkable vendor
  useEffect(() => { if (vendorId) setParams({ vendor: vendorId }, { replace: true }); }, [vendorId]); // eslint-disable-line

  const send = async () => {
    if (!vendorId) return toast.error("Select a vendor");
    let payload;
    try { payload = JSON.parse(payloadText); }
    catch (e) { return toast.error("Request payload is not valid JSON"); }
    setSending(true);
    try {
      const r = await api.post(`/pos-vendors/${vendorId}/playground/send`, {
        operation: op,
        request_payload: payload,
        mock,
        override_endpoint: endpointOverride || null,
      });
      setResult(r.data);
      const okish = r.data.status_code >= 200 && r.data.status_code < 300;
      if (okish) toast.success(`${r.data.status_code} · ${r.data.response_time_ms} ms${r.data.mock ? " · mock" : ""}`);
      else toast.warning(`${r.data.status_code} · ${r.data.error_reason || "non-2xx"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const loadHistory = async () => {
    try {
      const r = await api.get(`/pos-vendors/${vendorId || "__all__"}/history`, { params: { limit: 30 } });
      setHistory(r.data.history || []);
      setShowHistory(true);
    } catch (e) {
      toast.error("History load failed");
    }
  };

  const copyPayload = () => {
    navigator.clipboard.writeText(payloadText);
    toast.success("Copied");
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="pos-playground-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Operations · POS Playground</div>
          <h1 className="font-display text-3xl text-white flex items-center gap-3 mt-1">
            <FlaskConical className="w-6 h-6 kazo-text-champagne" />
            POS API Tester
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-3xl">
            Pick a vendor + a canonical operation, edit the request, send it to the
            vendor's real sandbox — or the built-in <span className="text-amber-300">Mock POS</span> that
            simulates a response with no external dependency. Every call is logged in the API Monitor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/pos-vendors"
            className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm border border-white/10 text-white hover:bg-white/10"
            data-testid="link-pos-vendors"
          >
            <Server className="w-4 h-4" /> Vendor Profiles
          </Link>
          <button onClick={loadHistory} className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm border border-white/10 text-white hover:bg-white/10" data-testid="btn-history">
            <History className="w-4 h-4" /> History
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LEFT — request */}
        <div className="rounded-sm border border-white/10 bg-black/30 p-4" data-testid="playground-request">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Vendor</label>
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                data-testid="select-vendor"
              >
                <option value="">Select…</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vendor_name}{v.is_sandbox ? "  (sandbox)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Canonical operation</label>
              <select
                value={op}
                onChange={(e) => setOp(e.target.value)}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                data-testid="select-operation"
              >
                {ops.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedOp && (
            <div className="text-[11px] text-white/50 mb-3 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{selectedOp.description} — required: <code className="text-amber-300/90">{(selectedOp.required_fields || []).join(", ")}</code></span>
            </div>
          )}

          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Endpoint (override)</label>
            <input
              value={endpointOverride}
              onChange={(e) => setEndpointOverride(e.target.value)}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm font-mono"
              data-testid="input-endpoint-override"
            />
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-white/40">Request payload (vendor schema)</label>
              <button onClick={copyPayload} className="text-[10px] text-white/50 hover:text-white flex items-center gap-1" data-testid="btn-copy-payload">
                <ClipboardCopy className="w-3 h-3" /> Copy
              </button>
            </div>
            <textarea
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              rows={16}
              className="w-full font-mono text-[11px] bg-black/50 border border-white/10 rounded-sm p-3 text-white/85"
              data-testid="input-request-payload"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs text-white flex items-center gap-2">
              <input type="checkbox" checked={mock} onChange={(e) => setMock(e.target.checked)} data-testid="chk-mock-mode" />
              Use Mock POS (simulated response — no external call)
            </label>
            <button
              disabled={sending}
              onClick={send}
              className="flex items-center gap-2 px-5 py-2 text-sm bg-amber-300 text-black rounded-sm font-semibold disabled:opacity-50"
              data-testid="btn-send-request"
            >
              {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Send
            </button>
          </div>

          {selectedVendor && !mock && !selectedVendor.base_url && (
            <div className="mt-3 text-[11px] text-amber-300 bg-amber-300/10 border border-amber-300/30 rounded-sm p-2">
              This vendor has no <code>base_url</code>. Set one in the vendor profile, or tick Mock POS to simulate.
            </div>
          )}
        </div>

        {/* RIGHT — response */}
        <div className="rounded-sm border border-white/10 bg-black/30 p-4" data-testid="playground-response">
          {!result ? (
            <div className="p-10 text-center text-white/40">
              <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Send a request to see the vendor's raw response and our field-mapping diff.
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-4">
                {result.status_code >= 200 && result.status_code < 300 ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <div className="text-sm text-white">
                    Status: <span className={result.status_code >= 200 && result.status_code < 300 ? "text-emerald-400" : "text-red-400"}>{result.status_code}</span>
                    <span className="text-white/40 mx-2">·</span>
                    <Clock className="inline w-3.5 h-3.5 text-white/40 -mt-1" /> {result.response_time_ms} ms
                    {result.mock && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-300/40 px-1.5 py-0.5 rounded-sm">mock</span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/40 truncate font-mono mt-0.5">{result.endpoint}</div>
                </div>
              </div>

              {result.error_reason && (
                <div className="mb-3 text-[11px] text-red-300 bg-red-500/10 border border-red-400/30 p-2 rounded-sm">
                  {result.error_reason}
                </div>
              )}

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Vendor response</div>
                <pre className="text-[11px] bg-black/50 border border-white/10 rounded-sm p-3 text-white/85 max-h-80 overflow-auto whitespace-pre-wrap">
{JSON.stringify(result.response_payload, null, 2)}
                </pre>
              </div>

              <MappingDiff diff={result.mapping_diff} />
            </div>
          )}
        </div>
      </div>

      {/* HISTORY panel */}
      {showHistory && (
        <div className="mt-4 rounded-sm border border-white/10 bg-black/30 p-4" data-testid="playground-history">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-white flex items-center gap-2"><History className="w-4 h-4" /> Recent calls</div>
            <button onClick={() => setShowHistory(false)} className="text-xs text-white/50 hover:text-white">Close</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-white/40">
                <tr>
                  <th className="text-left p-1.5">Time</th>
                  <th className="text-left p-1.5">Vendor</th>
                  <th className="text-left p-1.5">Operation</th>
                  <th className="text-left p-1.5">Status</th>
                  <th className="text-left p-1.5">Latency</th>
                  <th className="text-left p-1.5">Endpoint</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan="6" className="p-3 text-center text-white/40">No history yet.</td></tr>
                )}
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-white/5 text-white/70">
                    <td className="p-1.5 whitespace-nowrap">{h.timestamp?.replace("T", " ").slice(0, 19)}</td>
                    <td className="p-1.5">{h.vendor_name}</td>
                    <td className="p-1.5">{h.operation}</td>
                    <td className={`p-1.5 ${h.status_code >= 200 && h.status_code < 300 ? "text-emerald-400" : "text-red-400"}`}>{h.status_code}</td>
                    <td className="p-1.5">{h.response_time_ms} ms</td>
                    <td className="p-1.5 font-mono text-[10px] max-w-[380px] truncate">{h.endpoint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function MappingDiff({ diff }) {
  if (!diff) return null;
  const {
    required = [], optional = [], provided_vendor_fields = [],
    missing_required_canonical = [], extra_vendor_fields = [],
    unmapped_required_canonical = [], unmapped_optional_canonical = [],
    field_map = {},
  } = diff;
  const contractOk = missing_required_canonical.length === 0 && unmapped_required_canonical.length === 0;
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40 mb-1">
        <FileText className="w-3 h-3" /> Field-mapping diff
        {contractOk ? (
          <span className="ml-2 text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> contract satisfied</span>
        ) : (
          <span className="ml-2 text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> contract issues</span>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-2 text-[11px]">
        <DiffChip title="Required (canonical)" items={required} tone="neutral" />
        <DiffChip title="Optional (canonical)" items={optional} tone="neutral" />
        <DiffChip title="Missing required in payload" items={missing_required_canonical} tone="danger" />
        <DiffChip title="Extra vendor fields (informational)" items={extra_vendor_fields} tone="warn" />
        <DiffChip title="Unmapped required (add to field_map)" items={unmapped_required_canonical} tone="danger" />
        <DiffChip title="Unmapped optional" items={unmapped_optional_canonical} tone="neutral" />
      </div>
      <div className="mt-2 text-[10px] text-white/40">
        Provided vendor fields: <span className="text-white/70">{provided_vendor_fields.join(", ") || "—"}</span>
      </div>
      {Object.keys(field_map).length > 0 && (
        <details className="mt-2 text-[11px] text-white/60">
          <summary className="cursor-pointer">Field map used for this op</summary>
          <pre className="mt-1 bg-black/50 border border-white/10 rounded-sm p-2 text-white/70 whitespace-pre-wrap">
{JSON.stringify(field_map, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function DiffChip({ title, items, tone }) {
  const toneCls = tone === "danger" ? "border-red-400/40 text-red-300 bg-red-500/5"
                : tone === "warn"   ? "border-amber-300/40 text-amber-200 bg-amber-300/5"
                : "border-white/10 text-white/70 bg-white/5";
  return (
    <div className={`border rounded-sm p-2 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70 mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {items.length === 0 ? <span className="opacity-40">—</span> : items.map((i) => (
          <span key={i} className="px-1.5 py-0.5 rounded-sm bg-black/30 border border-white/10">{i}</span>
        ))}
      </div>
    </div>
  );
}
